// ---------------------------------------------------------------------------
// EventDrawer — right-hand slide-over with the full detail of one bell event:
// scheduled vs actual time, delay, strikes, zones, controller and failure info,
// plus "Ring now" / "Acknowledge" actions.
// ---------------------------------------------------------------------------
import React, {useEffect, useState} from "react";
import {CONFIG} from "../constants/BellStatusConstants";
import {parseISO, statusMeta} from "../utils/bellStatusUtils";

const BellIcon = () => (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
        <path
            d="M8 2.6a3.6 3.6 0 0 0-3.6 3.6c0 3-1.4 4-1.4 4h10s-1.4-1-1.4-4A3.6 3.6 0 0 0 8 2.6zM6.6 12.4a1.5 1.5 0 0 0 2.8 0"
            stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
        />
    </svg>
);

function EventDrawer({ctx, i18n, onClose, onRing, onAck}) {
    const [open, setOpen] = useState(false);

    // Trigger the enter transition on the frame after mount.
    useEffect(() => {
        if (ctx) {
            const id = requestAnimationFrame(() => setOpen(true));
            return () => cancelAnimationFrame(id);
        }
        setOpen(false);
    }, [ctx]);

    if (!ctx) return null;

    const {t, num, mon, days, lang} = i18n;
    const {data, dateKey, periodId} = ctx;
    const d = data.days.find((x) => x.date === dateKey);
    const p = data.periods.find((x) => x.id === periodId);
    const e = (d.events || []).find((x) => x.period_id === periodId);
    if (!p || !e) return null;

    const dt = parseISO(dateKey);
    const m = statusMeta(e.status, t);
    const school = data.school;
    const device = school.device || {};

    const delayText =
        e.delay_sec === null || e.delay_sec === undefined
            ? "—"
            : e.delay_sec < CONFIG.LATE_THRESHOLD_SEC
                ? t("onTime")
                : num(e.delay_sec) + t("secLate");

    const rows = [
        [t("time"), num(e.scheduled_at)],
        [t("actual"), e.actual_at ? num(e.actual_at) : "—"],
        [t("delay"), delayText],
        [t("strikes"), `${num(e.strikes_played || 0)} / ${num(p.strikes)}`],
        [t("zonesL"), (e.zones || p.zones || []).join(", ") || "—"],
        [t("devState"), e.device_online ? t("online") : t("offlineW")],
    ];

    return (
        <>
            <div className={`scrim ${open ? "open" : ""}`} onClick={onClose} />
            <aside className={`drawer ${open ? "open" : ""}`} role="dialog" aria-modal="true">
                <div className="dhead">
                    <button className="close" aria-label={t("close")} onClick={onClose}>
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                            <path d="M5 5l8 8M13 5l-8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                        </svg>
                    </button>
                    <div className="eyebrow">
                        {(lang === "ne" && school.name_ne ? school.name_ne : school.name) + " · " + school.code}
                    </div>
                    <h2>{lang === "ne" && p.name_ne ? p.name_ne : p.name}</h2>
                    <div className="when">
                        {`${days[dt.getDay()]} · ${num(dt.getDate())} ${mon(dt.getMonth())} ${num(dt.getFullYear())}`}
                    </div>
                    <div className="dbadge" style={{background: m.bg, color: m.ink}}>
                        <span className="dot" style={{background: m.color, boxShadow: "none"}} />
                        {m.label}
                    </div>
                </div>

                <div className="dbody">
                    <dl style={{margin: 0}}>
                        {rows.map(([k, v]) => (
                            <div className="drow" key={k}>
                                <dt>{k}</dt>
                                <dd>{v}</dd>
                            </div>
                        ))}
                    </dl>

                    {e.error_code ? (
                        <>
                            <div className="dsec">{t("err")}</div>
                            <div className="errbox">
                                <div className="ec">{e.error_code}</div>
                                <div className="em">{e.error_message || ""}</div>
                            </div>
                        </>
                    ) : null}

                    <div className="dsec">{t("controller")}</div>
                    <dl style={{margin: 0}}>
                        <div className="drow"><dt>ID</dt><dd>{device.id}</dd></div>
                        <div className="drow"><dt>{t("model")}</dt><dd>{device.model}</dd></div>
                        <div className="drow"><dt>{t("firmware")}</dt><dd>v{device.firmware}</dd></div>
                    </dl>
                </div>

                <div className="dfoot">
                    <button className="btn btn-primary" onClick={() => onRing(p.id, dateKey)}>
                        <BellIcon />
                        {t("rering")}
                    </button>
                    <button className="btn btn-quiet" onClick={onAck}>
                        {t("ack")}
                    </button>
                </div>
            </aside>
        </>
    );
}

export default EventDrawer;
